import * as React from 'react';

import { useResizeDetector } from 'react-resize-detector';

import ErrorPages from 'containers/Error/Pages';

import {
  ActivitiesContainer,
  ActivitiesContentContainer,
  ActivitiesFooterContainer,
  ContentContainer,
  RecordBodyContainer,
  RecordContainer,
  RecordHeaderContainer,
} from './styles';
import { RecordAction, RecordProps, RecordState } from './types';
import { pixelToInt, computeVerticalOffset, computeActivitiesHeight } from './utils';
import { VatixError } from '../../components/Error/types';

const initialState: RecordState = {
  windowHeight: 0,
  containerVerticalOffset: 0,
  maxHeaderHeight: 0,
  visibleHeaderHeight: 0,
  contentHeight: 0,
  activitiesVerticalOffset: 0,
};

function recordStateReducer(state: RecordState, action: RecordAction): RecordState {
  switch (action.type) {
    case 'updateContentHeight': {
      return {
        ...state,
        contentHeight: action.value,
      };
    }
    case 'updateHeaderHeight': {
      const { visibleHeaderHeight, maxHeaderHeight } = action.value;
      if (maxHeaderHeight) {
        return {
          ...state,
          maxHeaderHeight,
          visibleHeaderHeight,
        };
      }
      return {
        ...state,
        visibleHeaderHeight,
      };
    }
    case 'updateVerticalOffsets': {
      return {
        ...state,
        ...action.value,
      };
    }
    case 'updateWindowHeight': {
      return {
        ...state,
        windowHeight: action.value,
      };
    }
    case 'handleResize': {
      return {
        ...state,
        windowHeight: action.value.windowHeight,
        visibleHeaderHeight: action.value.headerHeight,
        maxHeaderHeight: action.value.headerHeight,
      };
    }
    default: {
      throw new Error('Unknown RecordAction');
    }
  }
}

const Record: React.FunctionComponent<RecordProps> = ({
  Header,
  Content,
  Activities,
  navBarHeight,
  disableMaxContentHeight = false,
  activitiesWidth,
}) => {
  const containerRef = React.useRef<HTMLElement>(null);
  const headerRef = React.useRef<HTMLElement>(null);
  const contentRef = React.useRef<HTMLDivElement>(null);
  const activitiesRef = React.useRef<HTMLElement>(null);

  const { width, ref, height: resizeDetectorHeight } = useResizeDetector({});
  const [maxActivitiesHeight, setMaxActivitiesHeight] = React.useState<undefined | number>();

  const [state, dispatch] = React.useReducer(recordStateReducer, initialState);
  const [error, setError] = React.useState<VatixError | null>(null);

  React.useEffect(() => {
    if (contentRef.current) {
      dispatch({ type: 'updateContentHeight', value: contentRef.current.clientHeight });
    }
  }, [contentRef]);

  const computeVisibleHeaderHeight = React.useCallback(
    (scrollOffset: number): number => {
      if (error !== null) {
        return 0;
      }
      if (!headerRef.current) {
        return 0;
      }
      const computedHeaderStyle = window.getComputedStyle(headerRef.current);
      return computeVerticalOffset(headerRef) + pixelToInt(computedHeaderStyle.height) + navBarHeight - scrollOffset;
    },
    [navBarHeight, error]
  );

  // initially set visibleHeaderHeight based on margins and content
  React.useEffect(() => {
    const headerHeight = computeVisibleHeaderHeight(0);

    dispatch({
      type: 'handleResize',
      value: {
        windowHeight: window.innerHeight,
        headerHeight,
      },
    });
  }, [computeVisibleHeaderHeight, headerRef, width]);

  // update activities vertical offset
  React.useEffect(() => {
    if (Activities && activitiesRef.current && containerRef.current) {
      const updatedContainerVerticalOffset = computeVerticalOffset(containerRef);
      const updatedActivitiesVerticalOffset = computeVerticalOffset(activitiesRef);

      dispatch({
        type: 'updateVerticalOffsets',
        value: {
          containerVerticalOffset: updatedContainerVerticalOffset,
          activitiesVerticalOffset: updatedActivitiesVerticalOffset + updatedContainerVerticalOffset - 20,
        },
      });
    }
  }, [containerRef, activitiesRef, Activities]);

  // handle resizing
  React.useEffect(() => {
    const handleWindowResize = (): void => {
      if (Activities) {
        const headerHeight = computeVisibleHeaderHeight(0);

        dispatch({
          type: 'handleResize',
          value: {
            windowHeight: window.innerHeight,
            headerHeight,
          },
        });
      }
    };

    window.addEventListener('resize', handleWindowResize);

    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  }, [Activities, computeVisibleHeaderHeight, containerRef, width]);

  // adjust visibleHeaderHeight on scroll
  React.useEffect(() => {
    const handleScroll = (): void => {
      if (Activities) {
        if (resizeDetectorHeight && resizeDetectorHeight > state.windowHeight) {
          dispatch({
            type: 'updateHeaderHeight',
            value: {
              visibleHeaderHeight:
                window.scrollY < state.maxHeaderHeight && state.visibleHeaderHeight >= 0
                  ? computeVisibleHeaderHeight(window.scrollY)
                  : 0,
            },
          });
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [
    Activities,
    computeVisibleHeaderHeight,
    resizeDetectorHeight,
    state.maxHeaderHeight,
    state.visibleHeaderHeight,
    state.windowHeight,
  ]);

  React.useEffect(() => {
    if (resizeDetectorHeight && resizeDetectorHeight <= state.windowHeight) {
      setMaxActivitiesHeight(() => resizeDetectorHeight - 40);
    } else {
      setMaxActivitiesHeight(undefined);
    }
  }, [resizeDetectorHeight, state.windowHeight]);

  if (error !== null) {
    return ErrorPages[error]({ error });
  }

  return (
    <RecordContainer ref={containerRef} id="record-container">
      <RecordHeaderContainer
        ref={headerRef}
        maxWidth={`${(contentRef.current?.clientWidth || 0) + (activitiesRef.current?.clientWidth || 0) + 10}px`}
      >
        {Header}
      </RecordHeaderContainer>

      <RecordBodyContainer ref={ref}>
        <ContentContainer
          ref={contentRef}
          $maxHeight={
            state.windowHeight > state.contentHeight && !disableMaxContentHeight ? `${state.contentHeight}px` : null
          }
        >
          {Content(setError)}
        </ContentContainer>

        {Activities ? (
          <ActivitiesContainer
            ref={activitiesRef}
            height={computeActivitiesHeight({
              ...state,
              maxActivitiesHeight,
            })}
            top={state.visibleHeaderHeight > 0 ? 0 : 10}
            $width={activitiesWidth}
          >
            {Activities?.Header}
            <ActivitiesContentContainer>{Activities?.Content}</ActivitiesContentContainer>
            {Activities.Footer ? <ActivitiesFooterContainer>{Activities.Footer}</ActivitiesFooterContainer> : null}
          </ActivitiesContainer>
        ) : null}
      </RecordBodyContainer>
    </RecordContainer>
  );
};

export default Record;
